import {
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  buildInputTableCols,
  CellFieldV2 as CellField,
  DvtFieldV2 as DvtField,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import {
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  Phi,
  Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import { roundNumber } from '@/lib/formatUtils';
import { FormMode } from '@/types/form';

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void,
  fetchDetail: (params: MuaHangRecord) => void,
  formMode: FormMode,
  cellError?: (rowUuid: string, field: string) => string | undefined
) => {
  const isViewMode = formMode === 'view';
  const columns = [
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 200,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTu1SearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục vật tư'
          value={params.row.ma_vt_data?.ma_vt}
          onRowSelection={async (row: VatTu) => {
            const data = await fetchDetail(row?.uuid || '');
            onCellValueChange(params.row.uuid, 'ma_vt_data', data);
          }}
          className='w-full'
          disabled={isViewMode}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_vt_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ten_vt',
      headerName: 'Tên hàng hóa - dịch vụ',
      width: 200,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='ten_vt' type='text' value={params.row.ma_vt_data?.ten_vt || ''} />
      ),
      nonWritable: true
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <DvtField row={params.row} onCellValueChange={onCellValueChange} disabled={isViewMode} />
      )
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 100,
      type: 'number',
      renderCell: (params: MuaHangRecord) => (
        <CellField
          name='so_luong'
          type='number'
          value={params.row.so_luong || 0}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
          disabled={isViewMode}
        />
      )
    },
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 120,
      type: 'number',
      renderCell: (params: MuaHangRecord) => (
        <CellField
          name='gia_nt'
          type='number'
          value={params.row.gia_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
          disabled={isViewMode}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 120,
      type: 'number',
      renderCell: (params: MuaHangRecord) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={isViewMode}
        />
      )
    },
    {
      field: 'ngay_giao',
      headerName: 'Ngày giao',
      width: 200,
      renderCell: (params: MuaHangRecord) => (
        <CellField
          name='ngay_giao'
          type='date'
          value={params.row.ngay_giao}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_giao', newValue)}
          disabled={isViewMode}
        />
      )
    },
    {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 250,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<Tax>
          searchEndpoint={`/${QUERY_KEYS.THUE}/`}
          searchColumns={thueSearchColumns}
          columnDisplay='ten_thue'
          dialogTitle='Danh mục thuế suất'
          value={params.row.ma_thue_data?.ten_thue}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
          className='w-full'
          disabled={isViewMode}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_thue',
        resultField: 'ma_thue_data',
        detailEndpoint: QUERY_KEYS.THUE
      }
    },
    {
      field: 'thue_suat',
      headerName: 'Thuế suất (%)',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='thue_suat' type='number' value={params.row.ma_thue_data?.thue_suat || 0.0} />
      ),
      nonWritable: true
    },
    {
      field: 'thue_nt',
      headerName: 'Thuế VND',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <CellField
          name='thue_nt'
          type='number'
          value={params.row.thue_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
          disabled={isViewMode}
        />
      )
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<BoPhan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          dialogTitle='Danh mục bộ phận'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_bp')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<VuViec>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
          searchColumns={vuViecSearchColumns}
          columnDisplay='ma_vu_viec'
          dialogTitle='Danh mục vụ việc'
          value={params.row.ma_vv_data?.ma_vv || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_vv')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vu_viec',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<HopDong>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
          searchColumns={hopDongSearchColumns}
          columnDisplay='ma_hd'
          dialogTitle='Danh mục hợp đồng'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_hd')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 140,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<DotThanhToan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
          searchColumns={dotThanhToanSearchColumns}
          columnDisplay='ma_dtt'
          dialogTitle='Danh mục đợt thanh toán'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_dtt')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<KheUoc>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
          searchColumns={kheUocSearchColumns}
          columnDisplay='ma_ku'
          dialogTitle='Danh mục khế ước'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_ku')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },

    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<Phi>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}/`}
          searchColumns={phiSearchColumns}
          columnDisplay='ma_phi'
          dialogTitle='Danh mục phí'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_phi')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục sản phẩm'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_sp')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 140,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<MuaHangRecord>
          type='text'
          searchEndpoint={'/'}
          searchColumns={lenhSanXuatSearchColumns}
          columnDisplay='ma_lsx'
          dialogTitle='Danh mục lệnh sản xuất'
          value={params.row.ma_lsx_data?.ma_lsx || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_lsx')}
          errorDisplay='tooltip'
        />
      )
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 180,
      renderCell: (params: MuaHangRecord) => (
        <SearchField<ChiPhiKhongHopLeData>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          columnDisplay='ma_cp_khl'
          dialogTitle='Danh mục chi phí không hợp lệ'
          value={params.row.ma_cp0_data?.ma_cpkhl || ''}
          onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
          className='w-full'
          disabled={isViewMode}
          error={cellError?.(params.row.uuid, 'ma_cp0')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp_khl',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'sl_hd',
      headerName: 'Số lượng hóa đơn',
      width: 140,
      renderCell: (params: MuaHangRecord) => <CellField name='sl_hd' type='text' value={params.row.sl_hd} disabled />,
      nonWritable: true
    },
    {
      field: 'sl_pn',
      headerName: 'Số lượng phiếu nhập',
      width: 160,
      renderCell: (params: MuaHangRecord) => <CellField name='sl_pn' type='text' value={params.row.sl_pn} disabled />,
      nonWritable: true
    },
    {
      field: 'so_ct_dh',
      headerName: 'Số đơn hàng',
      width: 140,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='so_ct_dh' type='text' value={params.row.so_ct_dh || ''} disabled={isViewMode} />
      )
    },
    {
      field: 'line_dh',
      headerName: 'Dòng',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='line_dh' type='text' value={params.row.line_dh || ''} disabled={isViewMode} />
      )
    },
    {
      field: 'so_ct_ss1',
      headerName: 'Số phiếu so sánh giá',
      width: 140,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='so_ct_ss1' type='text' value={params.row.so_ct_ss1 || ''} disabled={isViewMode} />
      )
    },
    {
      field: 'line_ss1',
      headerName: 'Dòng',
      width: 120,
      renderCell: (params: MuaHangRecord) => (
        <CellField name='line_ss1' type='text' value={params.row.line_ss1 || ''} disabled={isViewMode} />
      )
    }
  ];

  const visibleColumns = columns.filter(Boolean) as MuaHangRecord[];
  const tableColumns = buildInputTableCols(visibleColumns);

  return tableColumns.map((column, index) => ({
    ...column,
    pasteConfig: visibleColumns[index]?.pasteConfig,
    nonWritable: visibleColumns[index]?.nonWritable
  }));
};
